import React, { useEffect, useMemo, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Header from "components/Header/Header.js";
import HeaderLinks from "components/Header/HeaderLinks.js";
import Footer from "components/Footer/Footer.js";
import API from "utils/api";
import translations from "./translations.js";
import styles from "./productPageStyle.js";
import LangToggle from "./LangToggle.js";
import RailNav from "./Sections/RailNav.js";
import ProductHeroSection from "./Sections/ProductHeroSection.js";
import SealSection from "./Sections/SealSection.js";
import CertificationSection from "./Sections/CertificationSection.js";
import LimitsSection from "./Sections/LimitsSection.js";
import ReportBand from "./Sections/ReportBand.js";
import DescriptionSection from "./Sections/DescriptionSection.js";
import IngredientsSection from "./Sections/IngredientsSection.js";
import ProcessSection from "./Sections/ProcessSection.js";
import DocumentsSection from "./Sections/DocumentsSection.js";
import ComplaintDialog from "./ComplaintDialog.js";

const useStyles = makeStyles(styles);

// The record's sections are numbered as a register entry, and description and
// ingredients are both optional, so the numerals cannot be hard-coded: they
// are read off the list that is actually rendered, which is the same list the
// rail navigates.
const ROMAN = ["I", "II", "III", "IV", "V", "VI", "VII"];

export default function ProductPage(props) {
  const classes = useStyles();
  const { match, history, ...rest } = props;
  const authId = match.params.authId;

  const [lang, setLang] = useState("hu");
  const [status, setStatus] = useState("loading");
  const [product, setProduct] = useState(null);
  const [complaintOpen, setComplaintOpen] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const t = translations[lang];

  useEffect(() => {
    let cancelled = false;

    // An uncertified, expired or unknown product must not render under the
    // mark — /invalid-cert is the one place that message lives. A 404,
    // however, only means "no product carries this authId" — a damaged QR
    // code, a typo, a deleted product or an API not deployed yet produce the
    // exact same response as a genuinely uncertified product. Marking that
    // branch with reason=unknown lets /invalid-cert render a softer, merely
    // factual variant instead of asserting an invalid certification it
    // cannot actually back up.
    //
    // The redirect carries what this page already learned. /invalid-cert used
    // to re-request the same product, and because the endpoint records a scan
    // for every call, hit or miss, one failed scan wrote TWO rows into the
    // certifier's scan log. Handing the result over in router state means the
    // second request never happens.
    //
    // `verifiedByScan` also breaks the loop the other way: /invalid-cert
    // bounces a still-certified product back to /p/, and it must not do that
    // for a redirect this page just made.
    const toInvalid = (reason, productName) =>
      history.replace({
        pathname: "/invalid-cert",
        search:
          "?authId=" +
          encodeURIComponent(authId) +
          (reason ? "&reason=" + reason : ""),
        state: { verifiedByScan: true, productName: productName || null },
      });

    API.get("/product/ultragreen/" + encodeURIComponent(authId))
      .then((response) => {
        if (cancelled) return;
        if (!response.data || response.data.certValid !== true) {
          toInvalid(null, response.data ? response.data.name : null);
          return;
        }
        setProduct(response.data);
        setStatus("ready");
      })
      .catch((error) => {
        if (cancelled) return;
        if (error.response && error.response.status === 404) {
          toInvalid("unknown");
          return;
        }
        // A network or server failure is NOT an invalid certification — saying
        // so would be a false statement about a certified product.
        setStatus("error");
      });

    return () => {
      cancelled = true;
    };
  }, [authId, history]);

  const sections = useMemo(() => {
    if (!product) return [];
    const ingredients = product.ingredients || [];
    const defs = [
      { id: "section-certification", label: t.navCertification },
      { id: "section-limits", label: t.navNotCertification },
    ];
    if (product.description) {
      defs.push({ id: "section-description", label: t.navDescription });
    }
    if (ingredients.length > 0) {
      defs.push({
        id: "section-ingredients",
        label: `${t.navIngredients} · ${ingredients.length}`,
      });
    }
    defs.push({ id: "section-process", label: t.navProcess });
    defs.push({ id: "section-documents", label: t.navDocuments });
    return defs.map((d, i) => ({ ...d, num: ROMAN[i] }));
  }, [product, t]);

  // Scroll-spy for the rail. A rail that highlights a section the reader is
  // not looking at is worse than no rail.
  //
  // The rule is "the last section whose heading has passed the reading line",
  // not "the topmost section still on screen": at a boundary the second reads
  // as one section behind, because the tail of the previous section is still
  // clipping the top of the viewport while the reader is plainly into the
  // next heading.
  useEffect(() => {
    if (status !== "ready" || sections.length === 0) return undefined;

    const READING_LINE = 140;
    let frame = null;

    const update = () => {
      frame = null;

      // The last section's heading can never reach the reading line — the
      // document runs out first — so at the bottom of the page it is the
      // section being read by definition. Without this the rail would stay
      // stuck one entry short for the whole final screenful.
      const atBottom =
        window.innerHeight + window.pageYOffset >=
        document.documentElement.scrollHeight - 2;
      if (atBottom) {
        setActiveId(sections[sections.length - 1].id);
        return;
      }

      let current = sections[0].id;
      sections.forEach((s) => {
        const el = document.getElementById(s.id);
        if (el && el.getBoundingClientRect().top <= READING_LINE) {
          current = s.id;
        }
      });
      setActiveId(current);
    };

    const onScroll = () => {
      if (frame === null) frame = window.requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      if (frame !== null) window.cancelAnimationFrame(frame);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [status, sections]);

  const toggleLang = () => setLang((prev) => (prev === "hu" ? "en" : "hu"));

  const jumpTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const numOf = (id) => {
    const found = sections.find((s) => s.id === id);
    return found ? found.num : "";
  };

  return (
    <div className={classes.page}>
      <Header
        color="white"
        brand="UltraGreen"
        rightLinks={<HeaderLinks />}
        fixed
        {...rest}
      />

      {status !== "ready" && (
        <div className={`${classes.band} ${classes.bandGrow}`}>
          <div className={classes.inner}>
            <div className={classes.langRow}>
              <LangToggle t={t} onToggle={toggleLang} />
            </div>
            <div className={classes.stateBox}>
              {status === "loading" ? (
                t.loading
              ) : (
                <React.Fragment>
                  <h1 className={classes.stateTitle}>{t.errorTitle}</h1>
                  <p style={{ margin: 0 }}>
                    {t.errorBody}{" "}
                    <a href={"mailto:" + t.contactEmail}>{t.contactEmail}</a>
                  </p>
                </React.Fragment>
              )}
            </div>
          </div>
        </div>
      )}

      {status === "ready" && product && (
        <React.Fragment>
          <div className={classes.band}>
            <div className={classes.inner}>
              <div className={classes.langRow}>
                <LangToggle t={t} onToggle={toggleLang} />
              </div>
            </div>
          </div>

          <ProductHeroSection t={t} product={product} />
          <SealSection t={t} product={product} />

          <div className={classes.sheet}>
            <div className={classes.band}>
              <div className={classes.inner}>
                <div className={classes.sheetInner}>
                  <RailNav
                    t={t}
                    product={product}
                    sections={sections}
                    activeId={activeId}
                    onJump={jumpTo}
                  />

                  <div className={classes.content}>
                    <CertificationSection
                      t={t}
                      num={numOf("section-certification")}
                      category={product.category}
                    />
                    <LimitsSection t={t} num={numOf("section-limits")} />
                    {/* Placed here on purpose: the seven limits are where a
                        reader's doubt is created, so the way to report it
                        belongs immediately after them, not in the header. */}
                    <ReportBand
                      t={t}
                      onReportClick={() => setComplaintOpen(true)}
                    />
                    <DescriptionSection
                      t={t}
                      product={product}
                      num={numOf("section-description")}
                    />
                    <IngredientsSection
                      t={t}
                      product={product}
                      num={numOf("section-ingredients")}
                    />
                    <ProcessSection t={t} num={numOf("section-process")} />
                    <DocumentsSection t={t} num={numOf("section-documents")} />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <ComplaintDialog
            t={t}
            open={complaintOpen}
            onClose={() => setComplaintOpen(false)}
            productAuthId={product.authId}
          />
        </React.Fragment>
      )}

      <div className={classes.band}>
        <div className={classes.inner}>
          <p className={classes.companyLine}>{t.footerCompany}</p>
        </div>
      </div>

      <Footer />
    </div>
  );
}
